/**
  * @class meleActorH264
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-h264>` custom web element for playing H264 in <mele-channel>
  */

import meleActor from 'meleActor'
import { NAL, NALs, uint8ToNAL } from './nal'
import { BMFF } from './BMFF'

const PLAY_DELAY_MAX = 0.4
const PLAY_DELAY_FIX = 0.2

const MIME_TYPE = 'video/mp4; codecs="avc1.4d0029"' // main profile; actually works even if data are in baseline profile
const READY_STATE = ['NOTHING(0)     ', 'METADATA(1)    ', 'CURRENT_DATA(2)', 'FUTURE_DATA(3) ', 'ENOUGH_DATA(4) ']

const bg_image = require('./loading.gif')

TimeRanges.prototype.toString = function() {
  let str = ''
  for(let i=0; i<this.length; i++) str += `${this.start(i)}-${this.end(i)} `
  return str
}

if(typeof (SourceBuffer)!=='undefined') {
  SourceBuffer.prototype.toString = function() {
    const info = {
      appendWindowStart: this.appendWindowStart,
      appendWindowEnd: this.appendWindowEnd,
      buffered: String(this.buffered),
      timestampOffset: this.timestampOffset,
      updating: this.updating,
    }
    return Object.entries(info).map(([k, v])=>k+':'+v).join(' ')
  }
}

HTMLVideoElement.prototype.toString = function() {
  const info = {
    ready: READY_STATE[this.readyState],
    // network: NETWORK_STATE[el.networkState],
    seeking: this.seeking?1:0, // not needed as we don't seek
    paused: this.paused?1:0,
    ended: this.ended?1:0,
    error: this.error,
    currTime: this.currentTime,
    buffered: String(this.buffered),
    played: String(this.played), // not needed, always 0-currentTime
    // seekable: String(this.seekable), // not needed as we don't seek
    decoded: this.webkitDecodedFrameCount,
    dropped: this.webkitDroppedFrameCount,
    bytes: this.webkitVideoDecodedByteCount,
    expect: this.webkitVideoDecodedByteCountExpected,
    size: `${this.videoHeight}x${this.videoHeight}`,
  }
  let str = Object.entries(info).map(([k, v])=>k+':'+v).join(' ')
  if(this.played.length>0 && this.currentTime != this.played.end(0)) str += `CURRENT TIME ${this.currentTime} DIFFERS FROM PLAYTIME ${this.played.end(0)}`
  return str
}

const PROPERTIES = _.extend({}, {
  ets: { description: "event timestamp - 'optical' time of NAL in microseconds", type: 'integer', readonly: true },
  pts: { description: 'presentation timestamp - offset of NAL event time since first played NAL in microseconds', type: 'integer', readonly: true },
  edt: { description: 'event date time', type: 'string', readonly: true },
  videosize: { description: 'video resolution: WIDTHxHEIGHT', type: 'string', readonly: true },
  rendersize: { description: 'video render size: WIDTHxHEIGHT', type: 'string', readonly: true },
  ratio: { description: 'width/height ratio', type: 'string', readonly: true },
}, meleActor._properties)

export default class meleActorH264 extends meleActor {
  async init() {
    if(this.verbose>0) this.debug('INIT VIDEO')
    // setup internal variables
    this.verbose = this.parentElement.parentElement.verbose
    this.input_nals = new NALs()
    this.output_frames = new NALs()
    this.sps = null
    this.pps = null
    this.video_sps = null
    this.video_pps = null
    this.keyframe = 0
    this.processInputNALs_busy = 0
    this.processOutputFrames_busy = 0
    this.first_ets = undefined // event time of first displayed frame
    this.first_rts = undefined // real time when first frame was displayed
    this.prev_pts = undefined // presentation time of previous frame
    this.prev_mts = undefined // move time of previous frame when it was moved to source buffer
    this.frames_moved = 0
    this.webkitVideoDecodedByteCountExpected = 0
    this.lastClean = Date.now() // time of last cleaning of SourceBuffer

    // construct HTML video element
    const elVideo = this.elVideo = this.ownerDocument.createElement('video')
    // elVideo.onclick = ()=> {
    //   this.listener.publish(this.source, 'mmRefocus', { channel: this.selected })
    // }
    elVideo.poster = bg_image
    elVideo.muted = true // needed for autoplay
    this.appendChild(elVideo)

    if(/iP(ad|hone|od).+Version\/[\d.]+.*Safari/i.test(navigator.userAgent) || (/Safari/i.test(navigator.userAgent) && ('ontouchstart' in window))) {
      // Safari on iOS has problem handling object-fit: contain
      // this seems to help
      elVideo.addEventListener('loadedmetadata', ()=>{
        elVideo.style.objectFit = 'none'
        setTimeout(()=>{
          elVideo.style.objectFit = 'contain'
        }, 100)
      })
    }

    elVideo.addEventListener('click', this.refocus.bind(this), { passive: false })

    // setup callbacks
    this.assignEventsForInfo(this.elVideo)
    elVideo.requestVideoFrameCallback(this.frameInfo.bind(this))
    window.requestAnimationFrame(this.animationInfo.bind(this))
    elVideo.onerror = ()=>this.error(elVideo.error.message)

    // construct MediaSource
    const mediaSource = this.mediaSource = new MediaSource()
    elVideo.src = URL.createObjectURL(mediaSource)
    this.assignEventsForInfo(mediaSource)

    // consrtuct SourceBuffer
    await new Promise((resolve, reject) => {
      // TODO: this shall not be called multiple times !!!
      mediaSource.addEventListener('sourceopen', ()=>{
        try{
          const sourceBuffer = this.sourceBuffer = mediaSource.addSourceBuffer(MIME_TYPE)
          sourceBuffer.mode = 'segments' // "sequence"
          this.assignEventsForInfo(sourceBuffer)
          sourceBuffer.addEventListener('updateend', this.sourceBufferUpdated.bind(this))
          if(this.verbose>1) this.debug('source buffer created')
          resolve()
        }catch(e) {
          this.sourceBuffer = null
          reject(e)
        }
      })
    })

    if(this.verbose>0) this.debug('PLAY() VIDEO')
    elVideo.play()

    super.init()
  } // init

  async refocus() {
    try{
      const player = this.closest('mele-player')
      const channel = _.natural(this.parentElement.ch)
      await player.listener.request(player.source.replace('msysStreamer', 'msvcCameraAxis'), 'mmRefocus', { channel }, { request: 'mmSuccess', timeout: 10000 })
    }catch(e) {
      this.error('Error refocusing camera: '+e)
    }
  } // refocus

  async play() {
    await this.reset()
  } // play

  async playing() {
    // perform reset after information on beginning of new stream
    await this.reset()
  }

  async destroy() {
    if(this.verbose>0) this.debug('DESTROY VIDEO')
    if(!this.elVideo) return
    const elVideo = this.elVideo
    for(let i=0; i<5; i++) {
      // wait a bit while other source buffer operation in progress
      try{
        this.mediaSource.endOfStream()
      }catch(e) {
        await asyncTimeout(100)
        continue
      }
      break
    }
    if(this.sourceBuffer) this.mediaSource.removeSourceBuffer(this.sourceBuffer)
    delete this.sourceBuffer
    // elVideo.pause()
    // elVideo.load()
    URL.revokeObjectURL(elVideo.src)
    elVideo.removeAttribute('src')
    delete this.mediaSource
    this.elVideo.deleteElement()
    delete this.elVideo
  } // destroy

  reset() {
    if(this.resetting) return this.resetting
    if(this.resetted) return
    if(this.verbose>0) this.debug('RESET VIDEO')
    this.resetting = new Promise((resolve)=>{
      this.destroy()
        .finally(() => {
          this.init()
            .finally(()=>{
              delete this.resetting
              this.resetted = true
              resolve()
            })
        })
    })
    return this.resetting
  } // reset

  async message({ event_dt, params }) {
    if(this.resetted) {
      delete this.resetted
      this.elVideo.play()
    }
    const nal = params.payload
    if(!nal) return
    uint8ToNAL(nal)
    nal.ets = Math.floor(params.event_dt/1000)
    nal.its = Math.floor(event_dt/1000) // issued timestamp - time when NAL was published by mBox
    nal.lts = Math.floor(params.received_dt/1000) // load timestamp (when we have received the NAL) - this is added by mele-player on mmMessage arrival
    nal.id = params.id
    if(params.id>0 && this.elVideo) this.elVideo.last_frame_id = params.id
    this.input_nals.push(nal)
    this.processInputNALs()
  } // message

  async processInputNALs() {
    // we have to do a special processing to ensure proper order of NALs
    // asynchronous worker communication as well as message publishing can sometimes
    // cause that we receive messages in different order than produced
    // so we buffer them and always process only those, that are few miliseconds old
    // and order them according their actual event_dt and type of NAL (to ensure SPS>PPS>I frame order)
    // also we don't process frames before we get SPS+PPS and don't process P frames before getting I frame
    const PROCESSING_DELAY = 10
    if(this.processInputNALs_busy) return
    this.processInputNALs_busy = 1
    const output_frames_count = this.output_frames.length
    // eslint-disable-next-line no-constant-condition
    while(1) {
      if(this.input_nals.length===0) break

      // sort nals by event_dt and type
      this.input_nals.sort()

      // if I frame came, remove all preceding P frames as our processing does not keep up with pace of incoming data
      const deleted = this.input_nals.stripFramesBeforeKeyframe()
      if(deleted>0 && this.verbose>0) this.warn('Skipped', deleted, 'P frames to keep the pace')

      // if earliest NAL is not at least PROCESSING_DELAY old, sleep until it became such old
      // this helps to collect messages within PROCESSING_DELAY time window together
      // and together with sort() above ensure proper order of NALs even if they
      // don't arrive in the proper order
      const now = Date.now()
      const maturity = Date.now() - this.input_nals[0].lts
      if(maturity < PROCESSING_DELAY) {
        if(this.verbose>3) this.warn('Immature', this.input_nals[0].nal_type, 'dt', this.input_nals[0].lts, '+', PROCESSING_DELAY, ' > now', now, 'maturity', maturity)
        setTimeout(()=>this.processInputNALs(), PROCESSING_DELAY - maturity)
        break
      }

      const nal = this.input_nals.shift()
      const type = nal.type
      if(type==NAL.SPS) {
        if(this.verbose>2) this.debug('SPS found')
        if(this.sps && !this.sps.equals(nal)) {
          this.input_nals.unshift(nal)
          const input_nals = this.input_nals
          this.warn('Resetting video on SPS change')
          await this.reset()
          this.warn('Reseted video on SPS change')
          this.input_nals = input_nals
        }
        this.sps=nal
        continue
      }else if(type==NAL.PPS) {
        if(this.sps) {
          if(this.verbose>2) this.debug('PPS found')
          this.pps = nal
        } else if(this.verbose>1) this.debug('Skipping PPS before SPS')
        continue
      }else if(type==NAL.I) {
        if(!this.pps) {
          if(this.verbose>1) this.debug('Skipping I frame before PPS')
          continue
        }
        if(!this.keyframe) {
          if(this.verbose>1) this.debug('First keyframe found')
          this.keyframe=1
        }
      }else if(type==NAL.P) {
        if(!this.keyframe) {
          if(this.verbose>1) this.debug('Skipping P frame before I frame')
          continue
        }
      }else{
        if(this.verbose>0) this.warn('Skipping unsupported NAL type '+type)
        continue
      }
      nal.sps = this.sps
      nal.pps = this.pps
      this.output_frames.push(nal)
    }
    delete this.processInputNALs_busy

    // call processing of output frames
    if(output_frames_count !== this.output_frames.length) this.processOutputFrames()
  } // processInputNALs

  async processOutputFrames() {
    // here we have this.sps and this.pps properly set
    // and receive I/P frames properly ordered ensured very first will be I frame
    // we are called either from processInputNALs when some frames were added to this.output_frames
    // or from Source Buffer when it finished updating and is ready to get more data
    if(this.processOutputFrames_busy) return
    if(!this.sourceBuffer || this.sourceBuffer.updating) return
    if(this.output_frames.length===0) return
    if(this.elVideo.error) {
      if(this.verbose>0) this.error('processOutputFrames error:', this.elVideo.error.message)
      return
    }
    this.processOutputFrames_busy = 1

    const segments = []
    const processed_nals = []
    while(this.output_frames.length>0) {
      const nal = this.output_frames.shift()
      const { ets, sps, pps } = nal

      if(!(sps.equals(this.video_sps) && pps.equals(this.video_pps))) {
        // change of SPS or PPS compared to last set to video
        // so we have to create new MP4 initialization segment
        // and return - we will get back again after this is processed by Source Buffer
        this.video_sps = sps
        this.video_pps = pps
        if(this.verbose>1) this.debug('CREATING INIT SEGMENT')
        const seg = this.composeInitSegment(sps, pps)
        segments.push(seg)
        this.output_frames.unshift(nal)
        break
        // this.sourceBuffer.appendBuffer(seg) // this will turn on flag this.sourceBuffer.updating and prevent processOutputFrames() until source buffer is ready again
        // this.processOutputFrames_busy = 0
      }

      if(this.first_ets===undefined) {
        this.first_ets = ets
        this.first_rts = Date.now()
      }
      nal.pts = ets - this.first_ets // presentation timestamp: offset of our event time from very first I frame event time
      if(nal.pts < this.prev_pts) {
        if(this.verbose>0) this.error('Negative NAL pts: ${nal}')
        continue
      }
      if(isNaN(nal.duration)) {
        // cannot determine frame duration, end of processing frames
        if(this.output_frames.length===0) {
          this.output_frames.unshift(nal)
          break
        }
        const duration = this.output_frames[0].ets - ets
        if(duration<0) {
          if(this.verbose>0) this.error(`Negative duration: ${this.output_frames[0].ets} - ${ets} = ${duration}`)
          while(this.output_frames.length>0) this.output_frames.shift() // remove all frames
          processed_nals.length=0
          this.processOutputFrames_busy = 0
          return
        }
        nal.duration = duration
      }
      this.prev_pts = nal.pts
      const mts = Date.now()
      const real_delay = Date.now()-this.first_rts - nal.pts

      if(this.verbose>3) this.debug(`${nal} fake delay ${nal.fake_delay}  real delay ${real_delay}  from previous ${mts-this.prev_mts}`)
      this.prev_mts = mts

      const seg = this.composeFrameSegment(nal)
      this.webkitVideoDecodedByteCountExpected += seg.length - (nal.keyframe?70:108)
      segments.push(seg)
      processed_nals.push(nal)
    }

    const buf = new Uint8Array(segments.reduce((a, v)=>a+v.length, 0))
    let offset = 0
    for(const seg of segments) {
      if(this.verbose>3) this.debug('SEGMENT', seg.length)
      buf.set(seg, offset)
      offset += seg.length
    }

    if(buf.length>0) {
      if(this.verbose>3) this.debug('APPEND BUFFER: ', segments.length, 'SEGMENTS', buf.length, 'BYTES')
      if(!this.sourceBuffer) return
      // if(this.closest('mele-player').state==='paused') return
      this.sourceBuffer.appendBuffer(buf) // this will turn on flag this.sourceBuffer.updating and prevent processOutputFrames() until source buffer is ready again
    }

    // const buffered_ts = Date.now()
    // for(let nal of processed_nals){
    //   nal.mts = buffered_ts
    //   this.debug(`NAL gather: issue: ${nal.its-nal.ets} receive: ${nal.lts-nal.its} buffer: ${nal.mts-nal.lts}`)
    // }
    processed_nals.length=0

    this.processOutputFrames_busy = 0
  } // processOutputFrames

  sourceBufferUpdated() {
    if(this.verbose>3) this.warn('SOURCE BUFFER READY AGAIN')
    const sourceBuffer = this.sourceBuffer
    if(!sourceBuffer) return this.processOutputFrames()
    // if(this.closest('mele-player').state==='paused') return

    // clear old data in source buffer
    const now = Date.now()
    if(sourceBuffer.buffered.length>0 && this.elVideo.currentTime>=2 && (this.lastClean < now-10_000)) {
      this.lastClean = now
      const start = sourceBuffer.buffered.start(0)
      const new_start = Math.floor(this.elVideo.currentTime - 5.0)
      if(start<new_start) {
        if(this.verbose>2) this.warn('SOURCE BUFFER CLEARING', new_start)
        try{
          sourceBuffer.remove(0, new_start)
        }catch(e) {
          if(this.verbose>0) this.error(e)
        }
        return
      }
    }

    let buffer_end = 0
    for(let i=0; i<sourceBuffer.buffered.length; i++) {
      const end = sourceBuffer.buffered.end(i)
      if(buffer_end<end) buffer_end = end
    }

    if(this.elVideo.currentTime<buffer_end-PLAY_DELAY_MAX) {
      this.warn(`RESET CURRENT TIME: currentTime ${this.elVideo.currentTime} < buffer_end ${buffer_end} - PLAY_DELAY_MAX ${PLAY_DELAY_MAX}}`)
      this.elVideo.currentTime=buffer_end-PLAY_DELAY_FIX
    }

    this.processOutputFrames()
  } // sourceBufferUpdated

  composeInitSegment(sps_bytes, pps_bytes) {
    const sps = BMFF.parseSPS(sps_bytes)
    let moov = JSON.parse(BMFF.SEGMENT_TEMPLATES.moov)
    BMFF.findBox(moov, 'trak.tkhd').width = sps.width
    BMFF.findBox(moov, 'trak.tkhd').height = sps.height
    BMFF.findBox(moov, 'trak.mdia.minf.stbl.stsd.avc1').width = sps.width
    BMFF.findBox(moov, 'trak.mdia.minf.stbl.stsd.avc1').height = sps.height
    const avcC = BMFF.findBox(moov, 'trak.mdia.minf.stbl.stsd.avc1.avcC')
    avcC.sequenceParameterSets[0] = sps_bytes
    avcC.pictureParameterSets[0] = pps_bytes
    moov = BMFF.composeBox(moov)
    const init_segment = new Uint8Array(BMFF.FTYP_AS_BYTES.length+moov.length)
    init_segment.set(BMFF.FTYP_AS_BYTES, 0)
    init_segment.set(moov, BMFF.FTYP_AS_BYTES.length)
    return init_segment
  } // composeInitSegment

  composeFrameSegment(nal) {
    const { pts, duration, sps, pps } = nal
    const mdat_length = 20 + sps.length + pps.length + nal.length
    const defaultSampleDuration = duration * BMFF.MOOV_TIMESCALE
    const defaultSampleSize = mdat_length-8
    const baseMediaDecodeTime = pts * BMFF.MOOV_TIMESCALE

    const moof_template = nal.keyframe ? BMFF.MOOF_KEYFRAME_AS_BYTES : BMFF.MOOF_AS_BYTES
    const moof = new Uint8Array(moof_template.length)
    moof.set(moof_template)
    let view = new DataView(moof.buffer)
    view.setUint32(20, this.frames_moved++) // moof.mfhd.idx
    view.setUint32(48, defaultSampleDuration) // moof.traf.tfhd.duration
    view.setUint32(52, defaultSampleSize) // moof.traf.tfhd.size
    view.setUint32(72, parseInt(baseMediaDecodeTime / 0x100000000)) // moof.traf.tfdt.time - upper 32 bits
    view.setUint32(76, baseMediaDecodeTime % 0x100000000) // moof.traf.tfdt.time - lower 32 bits

    if(this.verbose>2) this.debug('SEGMENT', pts, 'dur', duration, 'BUFF '+this.sourceBuffer, 'VIDEO'+this.elVideo)

    const frame = new Uint8Array(moof.length+mdat_length)
    frame.set(moof)
    view = new DataView(frame.buffer, moof.length)
    view.setUint32(0, mdat_length) // complete size including size+type
    view.setUint8(4, 0x6D) // "m"
    view.setUint8(5, 0x64) // "d"
    view.setUint8(6, 0x61) // "a"
    view.setUint8(7, 0x74) // "t"
    view.setUint32(8, sps.length) // size for SPS
    frame.set(sps, moof.byteLength+12) // SPS content
    view.setUint32(12+sps.length, pps.length) // size for PPS
    frame.set(pps, moof.byteLength+16+sps.length) // PPS content
    view.setUint32(16+sps.length+pps.length, nal.length) // size for data
    frame.set(nal, moof.byteLength+20+sps.length+pps.length) // mdat data

    return frame
  } // composeFrameSegment

  frameInfo(now, metadata) {
    if(!this.isConnected) return // we were removed from DOM
    const pts = Math.round(metadata.mediaTime*1_000)
    if(pts*1000!==this.pts) {
      this.pts = pts*1000
      const ets = this.ets = (this.first_ets + pts)*1000
      this.edt = _.usec2t(ets)
      this.parentElement.vts = ets
    }

    this.ratio = NAL.aspectRatio(metadata.width, metadata.height)

    this.parentElement.videosize = this.videosize = metadata.width+'x'+metadata.height
    const ratio = metadata.width / metadata.height
    const ratio2 = this.clientWidth / this.clientHeight
    if(ratio > ratio2) {
      this.rendersize = this.clientWidth+'x'+Math.round(this.clientWidth/ratio)
    }else{
      this.rendersize = Math.round(this.clientHeight*ratio)+'x'+this.clientHeight
    }
    this.parentElement.rendersize = this.rendersize

    this.elVideo.requestVideoFrameCallback(this.frameInfo.bind(this))
    if(!(this.verbose>4)) return
  } // frameInfo

  animationInfo(now) {
    if(!(this.verbose>4)) return
    const el = this.elVideo
    const info = {
      ready: READY_STATE[el.readyState],
      currTime: el.currentTime,
      buffered: String(el.buffered),
      // played: String(el.played),
      decoded: el.webkitDecodedFrameCount,
      dropped: el.webkitDroppedFrameCount,
      bytes: el.webkitVideoDecodedByteCount,
      expect: this.webkitVideoDecodedByteCountExpected,
    }
    this.debug('ANIMATION now', now, Object.entries(info).map(([k, v])=>k+':'+v).join(' '))
    window.requestAnimationFrame(this.animationInfo.bind(this))
  } // animationInfo

  assignEventsForInfo(el) {
    let proto = el
    while((proto = proto.__proto__)) {
      for(const event of Object.keys(proto).filter(k=>/^on/.test(k))) {
        el.addEventListener(event.substr(2), this.eventInfo.bind(this, el), { passive: true })
      }
    }
  } // assignEventsForInfo

  eventInfo(src, event) {
    if(!(this.verbose>3)) return
    try{
      const { type, target: el, timeStamp } = event
      const time = String(_.roundBy(timeStamp, 3)).padStart(8, ' ')
      if(src!==el && this.verbose>2) this.warn('EVENT source and target differs: ', src, event)
      const target_type = el.constructor.name.toUpperCase()
      let str = `${target_type} EVENT [${time}] ${type}:`
      str = str.padEnd(52)
      let additional_info = ''
      if(target_type==='HTMLVIDEOELEMENT') {
        additional_info += String(el)
        // additional_info += '\n' + props.map(p=>p+'='+el[p]).join('   ')
        // additional_info += `\nbuffered: ${el.buffered}`
        // additional_info += `   played: ${el.played}`
        // additional_info += `   seekable: ${el.seekable}`
      }else if(target_type==='SOURCEBUFFER') {
        additional_info += String(el)
      }

      this.debug(str+additional_info)
    }catch(e) {
      // sometimes this is called when source buffer is already removed
      // we don't want to break but no error message is needed
    }
  } // eventInfo
} // class meleActorH264

meleActorH264._properties = PROPERTIES
meleActorH264.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-h264', meleActorH264)
